import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../../axiosInstance";
import "./ProfessionalTax.scss";
import { toast } from "react-toastify";
import LoadingScreen from "../../../components/LoadingScreen/LoadingScreen";
import WarningPopup from "../../../components/WarningPopup/WarningPopup";
import IconButton from "../../../components/IconButton/IconButton";

function ProfessionalTax() {
  const [branches, setBranches] = useState([]);
  const [slabs, setSlabs] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formState, setFormState] = useState({});
  const [warningPopup, setWarningPopup] = useState({
    isOpen: false,
    branchId: null,
    slabId: null,
    title: "",
    message: "",
  });

  const fetchSlabs = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_BASE_URL}/professional-tax-slabs`,
      );
      const slabsData = response.data || [];

      const slabsByBranch = slabsData.reduce((acc, branchData) => {
        if (branchData.branchId) {
          acc[branchData.branchId] = branchData.professionalTaxSlabs || [];
        }
        return acc;
      }, {});

      setSlabs(slabsByBranch);

      const initialForm = {};
      for (const branchId in slabsByBranch) {
        const branchSlabs = slabsByBranch[branchId];
        initialForm[branchId] = {
          slabs: branchSlabs.map((slab) => ({ ...slab, isEditing: false })),
          newSlab: getEmptySlabForm(),
        };
      }
      setFormState(initialForm);
    } catch (err) {
      console.error("Error fetching slabs:", err);
      setError("Failed to fetch professional tax slabs.");
    }
  }, []); // It has no external dependencies

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_API_BASE_URL}/branches/list`,
        );
        const branchesData = response.data.data || [];
        setBranches(branchesData);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching branches:", err);
        setError("Failed to fetch branches.");
        setLoading(false);
      }
    };

    fetchBranches();
    fetchSlabs();
  }, [fetchSlabs]); // FIX: Add fetchSlabs as a dependency

  const getEmptySlabForm = () => ({
    professionalTaxId: 0,
    slabName: "",
    slabSequence: 0,
    slabMinValue: 0,
    slabMaxValue: 0,
    slabAmount: 0,
    isNew: true,
  });

  const handleInputChange = (
    branchId,
    index,
    field,
    value,
    isNewSlab = false,
  ) => {
    setFormState((prev) => {
      const branchState = prev[branchId] || {
        slabs: [],
        newSlab: getEmptySlabForm(),
      };

      if (isNewSlab) {
        return {
          ...prev,
          [branchId]: {
            ...branchState,
            newSlab: {
              ...branchState.newSlab,
              [field]:
                field === "slabName" ? value : value === "" ? "" : +value,
            },
          },
        };
      } else {
        const updatedSlabs = [...branchState.slabs];
        updatedSlabs[index] = {
          ...updatedSlabs[index],
          [field]: field === "slabName" ? value : value === "" ? "" : +value,
          isEditing: true,
        };

        return {
          ...prev,
          [branchId]: {
            ...branchState,
            slabs: updatedSlabs,
          },
        };
      }
    });
  };

  const handleSaveNewSlab = async (branchId) => {
    const branchState = formState[branchId];
    if (!branchState) return;
    const newSlab = branchState.newSlab;

    if (!newSlab.slabName.trim()) {
      toast.error("Please enter a slab name");
      return;
    }
    if (newSlab.slabSequence === "" || newSlab.slabSequence < 0) {
      toast.error("Please enter a valid sequence number (0 or greater)");
      return;
    }
    if (newSlab.slabMinValue === "" || newSlab.slabMinValue < 0) {
      toast.error("Please enter a valid minimum value (0 or greater)");
      return;
    }
    if (newSlab.slabMaxValue === "" || newSlab.slabMaxValue < 0) {
      toast.error("Please enter a valid maximum value (0 or greater)");
      return;
    }
    if (newSlab.slabAmount === "" || newSlab.slabAmount < 0) {
      toast.error("Please enter a valid tax amount (0 or greater)");
      return;
    }

    const payload = [
      {
        professionalTaxId: 0,
        slabName: newSlab.slabName,
        slabSequence: +newSlab.slabSequence,
        slabMinValue: +newSlab.slabMinValue,
        slabMaxValue: +newSlab.slabMaxValue,
        slabAmount: +newSlab.slabAmount,
      },
    ];

    const endpoint = `${process.env.REACT_APP_API_BASE_URL}/professional-tax-slabs/branch/${branchId}`;

    try {
      await axiosInstance.put(endpoint, payload);
      toast.success("New slab created successfully!");
      setFormState((prev) => ({
        ...prev,
        [branchId]: {
          ...prev[branchId],
          newSlab: getEmptySlabForm(),
        },
      }));
      fetchSlabs();
    } catch (err) {
      console.error("Error saving new slab:", err);
    }
  };

  const handleUpdateSlab = async (branchId, index) => {
    const branchState = formState[branchId];
    if (!branchState) return;
    const slab = branchState.slabs[index];

    if (!slab.slabName.trim()) {
      toast.error("Please enter a slab name");
      return;
    }
    if (slab.slabSequence === "" || slab.slabSequence < 0) {
      toast.error("Please enter a valid sequence number (0 or greater)");
      return;
    }
    if (slab.slabMinValue === "" || slab.slabMinValue < 0) {
      toast.error("Please enter a valid minimum value (0 or greater)");
      return;
    }
    if (slab.slabMaxValue === "" || slab.slabMaxValue < 0) {
      toast.error("Please enter a valid maximum value (0 or greater)");
      return;
    }
    if (slab.slabAmount === "" || slab.slabAmount < 0) {
      toast.error("Please enter a valid tax amount (0 or greater)");
      return;
    }

    const payload = [
      {
        professionalTaxId: slab.professionalTaxId,
        slabName: slab.slabName,
        slabSequence: +slab.slabSequence,
        slabMinValue: +slab.slabMinValue,
        slabMaxValue: +slab.slabMaxValue,
        slabAmount: +slab.slabAmount,
      },
    ];

    const endpoint = `${process.env.REACT_APP_API_BASE_URL}/professional-tax-slabs/branch/${branchId}`;

    try {
      await axiosInstance.put(endpoint, payload);
      toast.success("Slab updated successfully!");
      fetchSlabs();
    } catch (err) {
      console.error("Error updating slab:", err);
    }
  };

  const isSlabIncomplete = (slab) => {
    return (
      !slab.slabName ||
      !slab.slabName.trim() ||
      slab.slabSequence === "" ||
      slab.slabSequence < 0 ||
      slab.slabMinValue === "" ||
      slab.slabMinValue < 0 ||
      slab.slabMaxValue === "" ||
      slab.slabMaxValue < 0 ||
      slab.slabAmount === "" ||
      slab.slabAmount < 0
    );
  };

  const handleDeleteSlab = (branchId, slabId, slabName) => {
    setWarningPopup({
      isOpen: true,
      branchId,
      slabId,
      title: "Delete Tax Slab",
      message: `Are you sure you want to delete the tax slab "${slabName}"? This action cannot be undone.`,
    });
  };

  const confirmDeleteSlab = async () => {
    // FIX: Removed unused 'branchId'
    const { slabId } = warningPopup;

    try {
      await axiosInstance.delete(
        `${process.env.REACT_APP_API_BASE_URL}/professional-tax-slabs/${slabId}`,
      );
      toast.success("Slab deleted successfully!");
      fetchSlabs();
    } catch (err) {
      console.error("Error deleting slab:", err);
    } finally {
      setWarningPopup({
        isOpen: false,
        branchId: null,
        slabId: null,
        title: "",
        message: "",
      });
    }
  };

  const handleWarningClose = () => {
    setWarningPopup({
      isOpen: false,
      branchId: null,
      slabId: null,
      title: "",
      message: "",
    });
  };

  const isSlabChanged = (currentSlab, originalSlab) => {
    return (
      currentSlab.slabName !== originalSlab.slabName ||
      currentSlab.slabSequence !== originalSlab.slabSequence ||
      currentSlab.slabMinValue !== originalSlab.slabMinValue ||
      currentSlab.slabMaxValue !== originalSlab.slabMaxValue ||
      currentSlab.slabAmount !== originalSlab.slabAmount
    );
  };

  if (loading) return <LoadingScreen />;

  return (
    <div className="professional-tax-container">
      <div className="header-section">
        <h1 className="display6">Professional Tax Management</h1>
        <p className="page-subtitle">
          Configure professional tax slabs for each branch
        </p>
      </div>

      {error && <div className="error-message">{error}</div>}

      {branches.map((branch) => {
        const branchId = branch.branchId;
        const originalSlabs = slabs[branchId] || [];
        const branchState = formState[branchId] || {
          slabs: [],
          newSlab: getEmptySlabForm(),
        };
        const { slabs: currentSlabs, newSlab } = branchState;

        return (
          <div className="branch-container" key={branchId}>
            <div className="diaply6">
              <h3 className="branch-title">{branch.branchName}</h3>
              <span className="slab-count">
                {originalSlabs.length} tax slab(s)
              </span>
            </div>

            {originalSlabs.length > 0 && (
              <div className="existing-slabs">
                <h4 className="display6">Existing Tax Slabs</h4>
                <div className="slabs-table">
                  <div className="slab-header-row">
                    <div className="slab-header-cell">Slab Name</div>
                    <div className="slab-header-cell">Sequence</div>
                    <div className="slab-header-cell">Min Value</div>
                    <div className="slab-header-cell">Max Value</div>
                    <div className="slab-header-cell">Tax Amount</div>
                    <div className="slab-header-cell">Actions</div>
                  </div>

                  {originalSlabs.map((originalSlab, index) => {
                    const currentSlab = currentSlabs[index] || originalSlab;
                    const isChanged = isSlabChanged(currentSlab, originalSlab);
                    const isIncomplete = isSlabIncomplete(currentSlab);

                    return (
                      <div
                        className="slab-row"
                        key={originalSlab.professionalTaxId || index}
                      >
                        <div
                          className="slab-cell slab-input-cell"
                          data-label="Slab Name"
                        >
                          <input
                            type="text"
                            className="form-input"
                            value={currentSlab.slabName}
                            onChange={(e) =>
                              handleInputChange(
                                branchId,
                                index,
                                "slabName",
                                e.target.value,
                              )
                            }
                          />
                        </div>
                        <div
                          className="slab-cell slab-input-cell"
                          data-label="Sequence"
                        >
                          <input
                            type="number"
                            className="form-input"
                            value={currentSlab.slabSequence}
                            min="0"
                            onChange={(e) =>
                              handleInputChange(
                                branchId,
                                index,
                                "slabSequence",
                                e.target.value,
                              )
                            }
                          />
                        </div>
                        <div
                          className="slab-cell slab-input-cell"
                          data-label="Min Value"
                        >
                          <input
                            type="number"
                            className="form-input"
                            value={currentSlab.slabMinValue}
                            min="0"
                            onChange={(e) =>
                              handleInputChange(
                                branchId,
                                index,
                                "slabMinValue",
                                e.target.value,
                              )
                            }
                          />
                        </div>
                        <div
                          className="slab-cell slab-input-cell"
                          data-label="Max Value"
                        >
                          <input
                            type="number"
                            className="form-input"
                            value={currentSlab.slabMaxValue}
                            min="0"
                            onChange={(e) =>
                              handleInputChange(
                                branchId,
                                index,
                                "slabMaxValue",
                                e.target.value,
                              )
                            }
                          />
                        </div>
                        <div
                          className="slab-cell slab-input-cell"
                          data-label="Tax Amount"
                        >
                          <input
                            type="number"
                            className="form-input"
                            value={currentSlab.slabAmount}
                            min="0"
                            onChange={(e) =>
                              handleInputChange(
                                branchId,
                                index,
                                "slabAmount",
                                e.target.value,
                              )
                            }
                          />
                        </div>
                        <div className="slab-cell slab-actions-cell">
                          <div className="slab-actions">
                            {isChanged && !isIncomplete && (
                              <IconButton
                                icon={"mdi:check"}
                                variant="save"
                                onClick={() =>
                                  handleUpdateSlab(branchId, index)
                                }
                              />
                            )}
                            <IconButton
                              icon="fluent:delete-28-regular"
                              variant="delete"
                              onClick={() =>
                                handleDeleteSlab(
                                  branchId,
                                  originalSlab.professionalTaxId,
                                  originalSlab.slabName,
                                )
                              }
                              title="Delete Tax"
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="new-slab-section">
              <h4 className="section-title">Add New Tax Slab</h4>
              <div className="new-slab-card">
                <div className="form-grid">
                  <div className="form-field">
                    <span className="field-label">Slab Name</span>
                    <input
                      type="text"
                      className="form-input"
                      value={newSlab.slabName}
                      placeholder="Enter slab name"
                      onChange={(e) =>
                        handleInputChange(
                          branchId,
                          0,
                          "slabName",
                          e.target.value,
                          true,
                        )
                      }
                    />
                  </div>
                  <div className="form-field">
                    <span className="field-label">Sequence</span>
                    <input
                      type="number"
                      className="form-input"
                      value={newSlab.slabSequence}
                      placeholder="Enter sequence"
                      min="0"
                      onChange={(e) =>
                        handleInputChange(
                          branchId,
                          0,
                          "slabSequence",
                          e.target.value,
                          true,
                        )
                      }
                    />
                  </div>
                  <div className="form-field">
                    <span className="field-label">Min Value</span>
                    <input
                      type="number"
                      className="form-input"
                      value={newSlab.slabMinValue}
                      placeholder="Enter minimum value"
                      min="0"
                      onChange={(e) =>
                        handleInputChange(
                          branchId,
                          0,
                          "slabMinValue",
                          e.target.value,
                          true,
                        )
                      }
                    />
                  </div>
                  <div className="form-field">
                    <span className="field-label">Max Value</span>
                    <input
                      type="number"
                      className="form-input"
                      value={newSlab.slabMaxValue}
                      placeholder="Enter maximum value"
                      min="0"
                      onChange={(e) =>
                        handleInputChange(
                          branchId,
                          0,
                          "slabMaxValue",
                          e.target.value,
                          true,
                        )
                      }
                    />
                  </div>
                  <div className="form-field">
                    <span className="field-label">Tax Amount</span>
                    <input
                      type="number"
                      className="form-input"
                      value={newSlab.slabAmount}
                      placeholder="Enter tax amount"
                      min="0"
                      onChange={(e) =>
                        handleInputChange(
                          branchId,
                          0,
                          "slabAmount",
                          e.target.value,
                          true,
                        )
                      }
                    />
                  </div>
                </div>

                <div className="new-slab-actions">
                  <button
                    className="btn btn-success btn-block"
                    onClick={() => handleSaveNewSlab(branchId)}
                    disabled={isSlabIncomplete(newSlab)}
                  >
                    Add Tax Slab
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })}

      <WarningPopup
        isOpen={warningPopup.isOpen}
        onClose={handleWarningClose}
        onAccept={confirmDeleteSlab}
        onDecline={handleWarningClose}
        title={warningPopup.title}
        message={warningPopup.message}
        acceptText="Delete"
        declineText="Cancel"
      />
    </div>
  );
}

export default ProfessionalTax;
